/**
 * Subscribes to AG-UI CUSTOM events named "artifacts" from the agent
 * and exposes the latest artifact names as React state.
 *
 * The backend emits:
 *   { type: "CUSTOM", name: "artifacts", value: ["artifact_name_1", "artifact_name_2"] }
 *
 * These flow through the AG-UI subscriber pipeline but are ignored by the
 * assistant-ui runtime aggregator. We tap into them via agent.subscribe().
 */
import { useEffect, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";

export function useStreamingArtifacts(agent: HttpAgent): string[] | null {
  const [artifactNames, setArtifactNames] = useState<string[] | null>(null);

  useEffect(() => {
    const sub = agent.subscribe({
      onCustomEvent: ({ event }) => {
        if (event.name !== "artifacts") {
          return;
        }
        if (!Array.isArray(event.value)) {
          return;
        }
        setArtifactNames(event.value);
      },
      onRunStartedEvent: () => {
        // Reset on new run so stale artifacts from previous turn don't linger.
        setArtifactNames(null);
      },
    });

    return () => sub.unsubscribe();
  }, [agent]);

  return artifactNames;
}
